import type { InvoiceGenerationMode } from '@/types/invoice';
import type { Job } from '@/types/job';

type InvoiceGenerationJob = Pick<
  Job,
  'bookingType' | 'invoicingMethod' | 'invoicingMode' | 'invoicingSchedule' | 'recurringConfig'
>;

const GENERATION_LABELS: Record<InvoiceGenerationMode, string> = {
  AUTOMATIC: 'Automatic',
  PREPAID: 'Prepaid',
  QUICKBOOKS_IMPORT: 'QuickBooks Import',
  MONTHLY_BATCH: 'Monthly Batch',
  PER_VISIT: 'Per Visit',
  PER_VISIT_MONTHLY: 'Per Visit (Monthly)',
  MILESTONE: 'Milestone',
  FIXED_PRICE: 'Fixed Price',
  MANUAL: 'Manual',
};

export function resolveInvoiceGenerationMode(
  job?: InvoiceGenerationJob | null,
): InvoiceGenerationMode {
  if (!job) return 'MANUAL';
  const invoicingMode = String(job.invoicingMode ?? '').toUpperCase();
  const invoicingMethod = String(job.invoicingMethod ?? '').toUpperCase();
  const invoicingSchedule = String(job.invoicingSchedule ?? '').toUpperCase();
  const bookingType = String(job.bookingType ?? '').toUpperCase();

  if (bookingType === 'RECURRING') {
    // A series billed per visit invoices each visit — never the whole job at a
    // fixed price, which bills once and refuses every later visit. Subscription
    // and prepaid series keep their own method.
    const billingMode = job.recurringConfig?.billingMode;
    if (billingMode !== 'SUBSCRIPTION' && billingMode !== 'PREPAID') {
      return invoicingSchedule === 'LAST_DAY_OF_MONTH' ? 'PER_VISIT_MONTHLY' : 'PER_VISIT';
    }
    if (invoicingMethod === 'FIXED_PRICE') return 'FIXED_PRICE';
    if (invoicingMethod === 'PER_VISIT') {
      return invoicingSchedule === 'LAST_DAY_OF_MONTH' ? 'PER_VISIT_MONTHLY' : 'PER_VISIT';
    }
  }

  if (invoicingMode === 'MANUAL') return 'MANUAL';
  if (invoicingMode === 'MILESTONE') return 'MILESTONE';
  if (invoicingMode === 'FIXED_PRICE') return 'FIXED_PRICE';
  if (invoicingMode === 'PER_VISIT_MONTHLY') return 'PER_VISIT_MONTHLY';
  if (invoicingMode === 'PER_VISIT') return 'PER_VISIT';

  if (invoicingMethod === 'FIXED_PRICE') return 'FIXED_PRICE';
  if (invoicingMethod === 'PER_VISIT') {
    return invoicingSchedule === 'LAST_DAY_OF_MONTH' ? 'PER_VISIT_MONTHLY' : 'PER_VISIT';
  }
  return 'MANUAL';
}

export function formatInvoiceGenerationMode(mode?: InvoiceGenerationMode | string | null): string {
  if (!mode) return 'Manual';
  return (
    GENERATION_LABELS[mode as InvoiceGenerationMode] ??
    mode
      .split('_')
      .map((part) => part.charAt(0) + part.slice(1).toLowerCase())
      .join(' ')
  );
}

export function formatInvoiceGenerationSource(invoice: {
  generationMode?: InvoiceGenerationMode | string | null;
  createdBy?: string | null;
}): string {
  // Invoices created before AUTOMATIC was introduced still carry MANUAL.
  if (invoice.createdBy === 'system:auto-invoice') return 'Automatic';
  return formatInvoiceGenerationMode(invoice.generationMode);
}
